import React, { useState, useEffect } from 'react';
import { Modal, SearchInput, Button } from '../Common';

const RelatedPostsModal = ({ isOpen, onClose, articles, selectedIds, onSave }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(selectedIds || []);
  const [filteredArticles, setFilteredArticles] = useState([]);

  useEffect(() => {
    // Update selected when prop changes
    setSelected(selectedIds || []);
  }, [selectedIds]);

  useEffect(() => {
    // Filter articles based on search
    if (!articles || articles.length === 0) {
      setFilteredArticles([]);
      return;
    }

    const searchLower = search.toLowerCase().trim();
    if (!searchLower) {
      setFilteredArticles(articles);
      return;
    }

    const filtered = articles.filter(article => {
      const titleMatch = article.title?.toLowerCase().includes(searchLower);
      const categoryMatch = article.category?.name?.toLowerCase().includes(searchLower);
      const briefMatch = article.brief?.toLowerCase().includes(searchLower);
      return titleMatch || categoryMatch || briefMatch;
    });

    setFilteredArticles(filtered);
  }, [search, articles]);

  const handleToggle = (articleId) => {
    const id = articleId.toString();
    if (selected.includes(id)) {
      setSelected(selected.filter(sid => sid !== id));
    } else if (selected.length < 3) {
      setSelected([...selected, id]);
    }
  };

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  const handleCancel = () => {
    setSelected(selectedIds || []); // Reset to original
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Select Related Posts" size="lg">
      <div className="space-y-4">
        {/* Search Input */}
        <div>
          <SearchInput
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Search articles by title, category, or content..."
          />
        </div>

        {/* Selection Counter */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <span className="text-sm font-medium text-blue-900">
            Selected: {selected.length}/3
          </span>
          {selected.length >= 3 && (
            <span className="text-xs text-blue-700">
              Maximum reached - deselect to choose different articles
            </span>
          )}
        </div>

        {/* Articles List */}
        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
          {!articles || articles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No articles available</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No articles match your search</p>
              <button
                onClick={() => setSearch('')}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredArticles.map(article => {
                const articleId = article.id?.toString();
                const isSelected = selected.includes(articleId);
                const isDisabled = !isSelected && selected.length >= 3;

                return (
                  <label
                    key={article.id}
                    className={`flex items-start gap-3 p-4 transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 hover:bg-blue-100'
                        : isDisabled
                        ? 'bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(articleId)}
                      disabled={isDisabled}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                          {article.title}
                        </h4>
                        {article.category?.name && (
                          <span className="flex-shrink-0 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                            {article.category.name}
                          </span>
                        )}
                      </div>
                      {article.brief && (
                        <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                          {article.brief}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                        {article.created_on && (
                          <span>
                            {new Date(article.created_on).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                        {article.time_to_read && (
                          <span>• {article.time_to_read}</span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600">
            💡 <strong>Tip:</strong> Select up to 3 related articles that will be displayed at the end of this article to encourage readers to explore more content.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={selected.length === 0}>
            Save Selection ({selected.length})
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RelatedPostsModal;
